import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';

import {
  Box,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { get, isEmpty } from 'lodash';

import { getDatesOfFinalValues, getFinalValues } from '../calculations';
import { formatCurrencyFloat, formatFloat, formatPercentageString } from 'shared/utils/numberFormatters';
import { CapRateConclusion, RentLossItem } from 'types/underwriting/income-approach/cap-rate-conclusion';

import { LOSS_ITEM_KEYS, RENT_LOSS_LABELS } from '../constants';
import { getInputStyles } from './IncomeAdjustmentRow';
import { CommercialRentRollUnit, ResidentialRentRollUnit } from 'types/underwriting/income-approach';
import { getRentLossPath, getRentLossRowTitle } from '../helpers';
import NumericInput from 'components/shared/inputs/NumericInput';
import { incomeApproachCalculations } from 'shared/calculations/incomeApproach';
import { UnderwritingOutput, ValueConclusion } from 'types/underwriting/underwriting';

type AdjustmentsAndLossesDisabledTableViewProps = {
  underwritingData: UnderwritingOutput;
};

const AdjustmentsAndLossesDisabledTableView = observer(
  ({ underwritingData }: AdjustmentsAndLossesDisabledTableViewProps) => {
    const commercialRentRoll = get(
      underwritingData,
      'incomeApproach.commercial.rentRoll',
      []
    ) as CommercialRentRollUnit[];
    const residentialRentRoll = get(
      underwritingData,
      'incomeApproach.residential.rentRoll',
      []
    ) as ResidentialRentRollUnit[];

    const capRateConclusion = get(underwritingData, 'incomeApproach.capRateConclusion', {} as CapRateConclusion);
    const {
      includeAsStabilizedDiscountRate,
      includeAsCompleteDiscountRate,
      asStabilizedDiscountPercentage,
      asCompleteDiscountPercentage,
      asStabilizedMonthsDiscounted,
      asCompleteMonthsDiscounted,
      capRate,
      npvAdjustments,
      asCompleteNpvAdjustments,
      asStabilizedNpvAdjustments,
      asStabilizedLossItems,
      asCompleteLossItems,
    } = capRateConclusion;

    const asStabilizedResidentialRentLossItems = get(
      underwritingData,
      getRentLossPath('residential', 'asStabilized'),
      []
    ) as RentLossItem[];

    const asCompleteResidentialRentLossItems = get(
      underwritingData,
      getRentLossPath('residential', 'asComplete'),
      []
    ) as RentLossItem[];

    const asStabilizedCommercialRentLossItems = get(
      underwritingData,
      getRentLossPath('commercial', 'asStabilized'),
      []
    ) as RentLossItem[];

    const asCompleteCommercialRentLossItems = get(
      underwritingData,
      getRentLossPath('commercial', 'asComplete'),
      []
    ) as RentLossItem[];

    const valueConclusions = get(underwritingData, 'settings.valueConclusions', []) as ValueConclusion[];
    const valueAsStabilized = valueConclusions.includes('asStabilized');
    const valueAsComplete = valueConclusions.includes('asComplete');

    const netOperatingIncome = useMemo(
      () => incomeApproachCalculations.calculateNetOperatingIncome(underwritingData || ({} as UnderwritingOutput)),
      [underwritingData]
    );

    const {
      capitalizationConclusion,
      capitalizationConclusionRounded,
      finalValueAsStabilized,
      finalValueAsStabilizedRounded,
      finalValueAsStabilizedPerUnit,
      finalValueAsStabilizedPerSf,
      finalValueAsComplete,
      finalValueAsCompleteRounded,
      finalValueAsCompletePerUnit,
      finalValueAsCompletePerSf,
      finalValueAsIs,
      finalValueAsIsRounded,
      finalValueAsIsPerUnit,
      finalValueAsIsPerSf,
      asCompleteDiscountRate,
      asStabilizedDiscountRate,
      asCompleteMarketValueWithDiscount,
      asStabilizedMarketValueWithDiscount,

      dateOfFinalValueAsIs,
      dateOfFinalValueAsComplete,
      dateOfFinalValueAsStabilized,
    } = useMemo(() => {
      return {
        ...getFinalValues(underwritingData),
        ...getDatesOfFinalValues(underwritingData),
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [underwritingData]);

    return (
      <Box>
        <Typography variant="subtitle1">
          <strong>Cap Rate Conclusion Summary Table</strong>
        </Typography>
        <Table>
          <TableHead sx={{ '& .MuiTableCell-head': { fontSize: 12, fontWeight: 700 } }}>
            <TableRow>
              <TableCell>Value</TableCell>
              <TableCell>Time Period</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Final Value (RD)</TableCell>
              <TableCell sx={{ width: 130 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Net Operating Income (NOI)</TableCell>
              <TableCell />
              <TableCell align="right">{formatCurrencyFloat(netOperatingIncome)}</TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell>Concluded Cap Rate</TableCell>
              <TableCell />
              <TableCell align="right">
                <NumericInput
                  sx={{ ...getInputStyles(true) }}
                  onFocus={(event) => event.target?.select()}
                  value={capRate || 0}
                  allowNegative={false}
                  autoComplete="off"
                  decimalScale={2}
                  fullWidth={false}
                  margin="none"
                  placeholder="0.00"
                  disabled
                  InputProps={{
                    sx: { paddingRight: 0 },
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>

            {!isEmpty(npvAdjustments) && (
              <TableRow>
                <TableCell>Capitalization Conclusion</TableCell>
                <TableCell />
                <TableCell align="right">{capitalizationConclusion}</TableCell>
                <TableCell align="right">{capitalizationConclusionRounded}</TableCell>
                <TableCell />
              </TableRow>
            )}

            {npvAdjustments.map((adjustment, index) => (
              <TableRow key={index}>
                <TableCell>{adjustment.name}</TableCell>
                <TableCell />
                <TableCell align="right">{formatCurrencyFloat(adjustment.amount)}</TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
            ))}

            {valueAsStabilized && (
              <React.Fragment>
                <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600 }, bgcolor: 'grey.200' }}>
                  <TableCell>Prospective Market Value As Stabilized</TableCell>
                  <TableCell>{dateOfFinalValueAsStabilized}</TableCell>
                  <TableCell align="right">{finalValueAsStabilized}</TableCell>
                  <TableCell align="right">{finalValueAsStabilizedRounded}</TableCell>
                  <TableCell />
                </TableRow>
                <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600 }, bgcolor: 'grey.200' }}>
                  <TableCell>Prospective Market Value As Stabilized Per Unit</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell align="right">{finalValueAsStabilizedPerUnit}</TableCell>
                  <TableCell />
                </TableRow>
                <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600 }, bgcolor: 'grey.200' }}>
                  <TableCell>Prospective Market Value As Stabilized Per SF</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell align="right">{finalValueAsStabilizedPerSf}</TableCell>
                  <TableCell />
                </TableRow>
                {includeAsStabilizedDiscountRate && (
                  <>
                    <TableRow>
                      <TableCell>Discount Rate of {formatPercentageString(asStabilizedDiscountPercentage)}</TableCell>
                      <TableCell>annualized, for {asStabilizedMonthsDiscounted} months</TableCell>
                      <TableCell align="right">{formatFloat(asStabilizedDiscountRate, 4)}</TableCell>
                      <TableCell />
                      <TableCell />
                    </TableRow>
                    <TableRow>
                      <TableCell>Market Value with discounting applied</TableCell>
                      <TableCell />
                      <TableCell align="right">{asStabilizedMarketValueWithDiscount}</TableCell>
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  </>
                )}

                {asStabilizedNpvAdjustments.map((adjustment, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell>{adjustment.name}</TableCell>
                      <TableCell />
                      <TableCell align="right">{formatCurrencyFloat(adjustment.amount)}</TableCell>
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  );
                })}

                {asStabilizedResidentialRentLossItems.map((rentLossItem, index) => (
                  <TableRow>
                    <TableCell>
                      Less {getRentLossRowTitle(rentLossItem, residentialRentRoll, RENT_LOSS_LABELS.RESIDENTIAL)}
                    </TableCell>
                    <TableCell>{rentLossItem.months} months</TableCell>
                    <TableCell align="right">{formatCurrencyFloat(rentLossItem.amount)}</TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                ))}

                {asStabilizedCommercialRentLossItems.map((rentLossItem, index) => (
                  <TableRow>
                    <TableCell>
                      Less {getRentLossRowTitle(rentLossItem, commercialRentRoll, RENT_LOSS_LABELS.COMMERCIAL)}
                    </TableCell>
                    <TableCell>{rentLossItem.months} months</TableCell>
                    <TableCell align="right">{formatCurrencyFloat(rentLossItem.amount)}</TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                ))}

                {asStabilizedLossItems.map((lossItem, index) => {
                  const isValueOnly = [
                    LOSS_ITEM_KEYS.ENTREPRENEURIAL_PROFIT,
                    LOSS_ITEM_KEYS.BUYOUT_COST,
                    LOSS_ITEM_KEYS.COMMISSION_FEE,
                  ].includes(lossItem.name);
                  const isEntrepreneurialProfit = lossItem.name === LOSS_ITEM_KEYS.ENTREPRENEURIAL_PROFIT;

                  return (
                    <TableRow>
                      <TableCell>Less {lossItem.name}</TableCell>
                      <TableCell align="right">{isValueOnly ? '' : lossItem.months}</TableCell>
                      <TableCell align="right" sx={{ position: 'relative' }}>
                        {isEntrepreneurialProfit
                          ? formatPercentageString(lossItem.amount || 0)
                          : formatCurrencyFloat(lossItem.amount)}
                      </TableCell>
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  );
                })}
              </React.Fragment>
            )}
            {valueAsComplete && (
              <>
                <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600 }, bgcolor: 'grey.200' }}>
                  <TableCell>Prospective Market Value As Complete</TableCell>
                  <TableCell>{dateOfFinalValueAsComplete}</TableCell>
                  <TableCell align="right">{finalValueAsComplete}</TableCell>
                  <TableCell align="right">{finalValueAsCompleteRounded}</TableCell>
                  <TableCell />
                </TableRow>
                <>
                  <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600 }, bgcolor: 'grey.200' }}>
                    <TableCell>Prospective Market Value As Complete Per Unit</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell align="right">{finalValueAsCompletePerUnit}</TableCell>
                    <TableCell />
                  </TableRow>
                  <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600 }, bgcolor: 'grey.200' }}>
                    <TableCell>Prospective Market Value As Complete Per SF</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell align="right">{finalValueAsCompletePerSf}</TableCell>
                    <TableCell />
                  </TableRow>
                </>
                {includeAsCompleteDiscountRate && (
                  <>
                    <TableRow>
                      <TableCell>Discount Rate of {formatPercentageString(asCompleteDiscountPercentage)}</TableCell>
                      <TableCell>annualized, for {asCompleteMonthsDiscounted} months</TableCell>
                      <TableCell align="right">{formatFloat(asCompleteDiscountRate, 4)}</TableCell>
                      <TableCell />
                      <TableCell />
                    </TableRow>
                    <TableRow>
                      <TableCell>Market Value with discounting applied</TableCell>
                      <TableCell />
                      <TableCell align="right">{asCompleteMarketValueWithDiscount}</TableCell>
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  </>
                )}
                {asCompleteNpvAdjustments.map((adjustment, index) => (
                  <TableRow key={index}>
                    <TableCell>{adjustment.name}</TableCell>
                    <TableCell />
                    <TableCell align="right">{formatCurrencyFloat(adjustment.amount)}</TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                ))}

                {asCompleteResidentialRentLossItems.map((rentLossItem, index) => (
                  <TableRow>
                    <TableCell>
                      Less {getRentLossRowTitle(rentLossItem, residentialRentRoll, RENT_LOSS_LABELS.RESIDENTIAL)}
                    </TableCell>
                    <TableCell>{rentLossItem.months} months</TableCell>
                    <TableCell align="right">{formatCurrencyFloat(rentLossItem.amount)}</TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                ))}

                {asCompleteCommercialRentLossItems.map((rentLossItem, index) => (
                  <TableRow>
                    <TableCell>
                      Less {getRentLossRowTitle(rentLossItem, commercialRentRoll, RENT_LOSS_LABELS.COMMERCIAL)}
                    </TableCell>
                    <TableCell>{rentLossItem.months} months</TableCell>
                    <TableCell align="right">{formatCurrencyFloat(rentLossItem.amount)}</TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                ))}

                {asCompleteLossItems.map((lossItem, index) => {
                  const isValueOnly = [
                    LOSS_ITEM_KEYS.ENTREPRENEURIAL_PROFIT,
                    LOSS_ITEM_KEYS.BUYOUT_COST,
                    LOSS_ITEM_KEYS.COMMISSION_FEE,
                  ].includes(lossItem.name);
                  const isEntrepreneurialProfit = lossItem.name === LOSS_ITEM_KEYS.ENTREPRENEURIAL_PROFIT;

                  return (
                    <TableRow>
                      <TableCell>Less {lossItem.name}</TableCell>
                      <TableCell align="right">{isValueOnly ? '' : lossItem.months}</TableCell>
                      <TableCell align="right" sx={{ position: 'relative' }}>
                        {isEntrepreneurialProfit
                          ? formatPercentageString(lossItem.amount || 0)
                          : formatCurrencyFloat(lossItem.amount)}
                      </TableCell>
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  );
                })}
              </>
            )}
          </TableBody>
          <TableFooter
            sx={{
              '& .MuiTableCell-root': { bgcolor: 'grey.200', color: 'text.primary', fontSize: 14, fontWeight: 600 },
            }}
          >
            <TableRow>
              <TableCell>As Is Market Value:</TableCell>
              <TableCell>{dateOfFinalValueAsIs}</TableCell>
              <TableCell align="right">{finalValueAsIs}</TableCell>
              <TableCell align="right">{finalValueAsIsRounded}</TableCell>
              <TableCell />
            </TableRow>
            <>
              <TableRow>
                <TableCell>As Is Market Value Per Unit</TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right">{finalValueAsIsPerUnit}</TableCell>
                <TableCell />
              </TableRow>
              <TableRow>
                <TableCell>As Is Market Value Per SF</TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right">{finalValueAsIsPerSf}</TableCell>
                <TableCell />
              </TableRow>
            </>
          </TableFooter>
        </Table>
      </Box>
    );
  }
);

export default AdjustmentsAndLossesDisabledTableView;
